/**
 * @fileoverview Adaptadores entre CR Auth y la forma de sesión que usa el resto del front.
 * La sesión (`state.user.user`) conserva la forma de la respuesta del login de ZS Backend,
 * así las pantallas de negocio no cambian.
 * @module interfaces/authMappers
 */

import { AUTH_ERROR_CODES } from '../integrations/models/auth.models'

/** Token de relleno para CR Auth local con RECAPTCHA_ENABLED=false (ENVIRONMENT=dev no carga reCAPTCHA). */
export const DEV_RECAPTCHA_TOKEN = 'dev-sin-recaptcha'

const DOCUMENT_TYPES = { 1: 'DNI', 2: 'CE' }
const CHANNELS = { 1: 'EMAIL', 2: 'SMS' }

/**
 * Tipo de documento de CR Auth a partir del tipo del front (1 DNI, 2 CE, 3 RUC).
 * @param {(number|string)} tipoDocumento
 * @returns {('DNI'|'CE'|null)} null si CR Auth no lo soporta (RUC o inválido)
 */
export function toDocumentType (tipoDocumento) {
  return DOCUMENT_TYPES[Number(tipoDocumento)] || null
}

/**
 * Canal de CR Auth a partir del medio de envío del front (1 correo, 2 celular).
 * @param {(number|string)} medioEnvio
 * @returns {('EMAIL'|'SMS'|null)}
 */
export function toChannel (medioEnvio) {
  return CHANNELS[Number(medioEnvio)] || null
}

/**
 * Token de reCAPTCHA a enviar. En local no hay reCAPTCHA y CR Auth exige el campo.
 * @param {string} token
 * @param {boolean} isDev
 * @returns {string}
 */
export function toRecaptchaToken (token, isDev) {
  if (token) return token
  return isDev ? DEV_RECAPTCHA_TOKEN : ''
}

/**
 * Sesión del front con vencimientos absolutos calculados con el reloj local al recibir los tokens,
 * para no depender del reloj del dispositivo frente al `exp` del JWT.
 * @param {import('../integrations/models/auth.models').SessionTokens} tokens
 * @param {number} now - Date.now() al recibir la respuesta
 * @returns {{accessToken: string, refreshToken: string, accessExpiresAt: number, refreshExpiresAt: number}}
 */
export function toSession (tokens, now) {
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    accessExpiresAt: now + (Number(tokens.expiresIn) || 0) * 1000,
    refreshExpiresAt: now + (Number(tokens.refreshExpiresIn) || 0) * 1000
  }
}

/**
 * Funcionalidades de CR Auth en la forma de ZS Backend (`mostrar`, `mensaje`).
 * `getPolizas` las reemplaza luego por la forma completa (con `titulo`, `imagen` y `bloqueado`).
 * @param {Object.<string, {show: boolean, message: (string|undefined)}>} features
 * @returns {Object.<string, {mostrar: boolean, mensaje: (string|undefined)}>}
 */
export function toLegacyFeatures (features) {
  return Object.keys(features || {}).reduce((legacy, name) => {
    legacy[name] = { mostrar: features[name].show, mensaje: features[name].message }
    return legacy
  }, {})
}

/**
 * Respuesta del login de CR Auth en la forma del login de ZS Backend.
 * @param {import('../integrations/models/auth.models').LoginResponse} result
 * @param {{accessToken: string, refreshExpiresAt: number}} session
 * @returns {import('./loginResponse').LoginResponse}
 */
export function toLegacyLoginResponse (result, session) {
  const user = result.user || {}
  const password = result.password || {}
  return {
    codigoRespuesta: '01',
    mensajeRespuesta: '',
    jwtToken: session.accessToken,
    exp: session.refreshExpiresAt,
    codigoUsuario: user.id,
    nombreUsuario: user.firstName || '',
    apellidoPaternoUsuario: user.lastName || '',
    apellidoMaternoUsuario: user.secondLastName || '',
    edad: user.age,
    claveProximaVencer: password.aboutToExpire === true,
    diasRestantesContraseña: password.aboutToExpire === true ? password.expiresInDays : null,
    funcionalidades: toLegacyFeatures(result.features)
  }
}

/**
 * Error del login de CR Auth en la forma que ya maneja `login.mixin` (`err.status` y `err.data`).
 * @param {import('../integrations/models/auth.models').IntegrationError} error
 * @returns {{status: number, data: Object, authError: Object}}
 */
export function toLegacyLoginError (error) {
  const code = error && error.code
  const details = (error && error.details) || {}
  let data = {}

  switch (code) {
  case AUTH_ERROR_CODES.AUTH_INVALID_CREDENTIALS:
    data = { codigoRespuesta: '99', intentosRestantes: details.remainingAttempts !== undefined ? details.remainingAttempts : null }
    break
  case AUTH_ERROR_CODES.VALIDATION_ERROR:
    data = { codigoRespuesta: '99', intentosRestantes: null }
    break
  case AUTH_ERROR_CODES.AUTH_ACCOUNT_LOCKED:
    data = { codigoRespuesta: '99', bloqueado: true, tipoBloqueo: 'BLOQUEO_INTENTOS' }
    break
  case AUTH_ERROR_CODES.AUTH_PASSWORD_EXPIRED:
    data = { claveVencida: true }
    break
  case AUTH_ERROR_CODES.AUTH_PASSWORD_NOT_SET:
    data = { claveNoRegistrada: true }
    break
  case AUTH_ERROR_CODES.AUTH_CAPTCHA_INVALID:
  case AUTH_ERROR_CODES.AUTH_CAPTCHA_PROVIDER_ERROR:
    data = { captchaInvalido: true }
    break
  }

  return { status: (error && error.status) || 0, data, authError: error }
}

/**
 * Error equivalente a la clave vencida, para el reseteo forzado (`mustChangePassword`).
 * @returns {{status: number, data: Object}}
 */
export function toForcedResetError () {
  return { status: 200, data: { claveVencida: true } }
}



// WEBPACK FOOTER //
// ./src/interfaces/authMappers.js